import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { getFullFieldName } from '~/core/application/workflow/utils/getFullFieldName';
import { EMPTY_ARR, EMPTY_OBJ } from '~/core/shared/constants';
import { FormFieldHooks } from '~/core/shared/types';
import { FormFieldBlockRendererProps } from '../../types/FormFieldBlockRendererProps';
import { useAsyncProps } from '../useAsyncProps';
import { getComponentFromWorkflowStepFormFieldType } from './logic/getComponentFromWorkflowStepFormFieldType';
import { getHandleSetMetaPropertiesOnFormFieldUpdate } from './logic/getHandleSetMetaPropertiesOnFormFieldUpdate';

export const useFormFieldBlockRenderer = (
  block: FormFieldBlockRendererProps['block'],
  namePrefixes: FormFieldBlockRendererProps['namePrefixes'] = EMPTY_ARR,
) => {
  const {
    componentType,
    description,
    helpText,
    readOnly,
    hideValidationLabel,
    label,
    labelProps,
    hideLabel,
    optionalText,
    componentProps = EMPTY_OBJ,
    validationRules,
  } = block;
  const { setValue } = useFormContext();
  const { asyncProps, isLoadingAsyncProps } = useAsyncProps(block);
  const component = useMemo(
    () => getComponentFromWorkflowStepFormFieldType(componentType),
    [componentType],
  );

  const name = useMemo(
    () => getFullFieldName(block.name, namePrefixes),
    [namePrefixes, block.name],
  );

  const hooks = useMemo((): FormFieldHooks | undefined => {
    if (!block.setsMetaProperties) return undefined;
    return {
      afterOnChange: getHandleSetMetaPropertiesOnFormFieldUpdate(setValue),
    };
  }, [block.setsMetaProperties, setValue]);

  return {
    component,
    name,
    hooks,
    type: componentType,
    description,
    helpText,
    required: validationRules?.required ?? false,
    readOnly,
    hideValidationLabel,
    label,
    labelProps,
    hideLabel,
    optionalText,
    ...componentProps,
    ...asyncProps,

    isLoadingAsyncProps,
  };
};
